// frontend/src/pages/StudentPreferences.js
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BACKEND_URL } from "../config";
import { safeGetItem } from "../utils/storage";
import { useAuth } from "../hooks/useAuth";

const emptyPrefs = {
  interestedCountries: [],
  preferredCourses: [],
  budget: "",
  studyPlan: "",
};

function splitList(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export default function StudentPreferences() {
  const { user, token } = useAuth();
  const email = user?.email || safeGetItem("studentEmail");

  const [prefs, setPrefs] = useState(emptyPrefs);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let alive = true;

    async function load() {
      if (!email || !token) return;
      try {
        const res = await fetch(`${BACKEND_URL}/student/preferences`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (alive) {
            setPrefs({
              interestedCountries: Array.isArray(data.interestedCountries) ? data.interestedCountries : [],
              preferredCourses: Array.isArray(data.preferredCourses) ? data.preferredCourses : [],
              budget: data.budget || "",
              studyPlan: data.studyPlan || "",
            });
          }
        }
      } catch (err) {
        console.error("Preferences load error", err);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [email, token]);

  const save = async () => {
    setSaving(true);
    setMessage("");
    try {
      const res = await fetch(`${BACKEND_URL}/student/preferences`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, ...prefs }),
      });
      if (!res.ok) throw new Error("Save failed");
      setMessage("Preferences saved.");
    } catch (err) {
      console.error("Preferences save error", err);
      setMessage("Could not save preferences.");
    } finally {
      setSaving(false);
    }
  };

  if (!email) return <div className="min-h-screen bg-slate-950 p-10 text-white">Please login.</div>;

  const inputClass =
    "mt-2 w-full rounded-xl border border-white/15 bg-white/[0.08] px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-cyan-300";

  return (
    <main className="min-h-screen bg-slate-950 px-4 py-8 text-white sm:px-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[4px] text-cyan-200">Study Preferences</p>
          <h1 className="mt-3 text-3xl font-semibold">Tell AbroadJacks what you are looking for</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
            These fields power your dashboard recommendations and help consultancies understand your goals.
          </p>
        </div>

        <section className="rounded-2xl border border-white/10 bg-white/[0.06] p-5">
          <div className="grid gap-5 md:grid-cols-2">
            <label className="block text-sm font-semibold text-slate-200">
              Preferred study countries
              <input
                className={inputClass}
                placeholder="UK, USA, Canada"
                value={prefs.interestedCountries.join(", ")}
                onChange={(event) =>
                  setPrefs((prev) => ({
                    ...prev,
                    interestedCountries: splitList(event.target.value),
                  }))
                }
              />
            </label>

            <label className="block text-sm font-semibold text-slate-200">
              Preferred courses
              <input
                className={inputClass}
                placeholder="MS, MBA, Data Science"
                value={prefs.preferredCourses.join(", ")}
                onChange={(event) =>
                  setPrefs((prev) => ({
                    ...prev,
                    preferredCourses: splitList(event.target.value),
                  }))
                }
              />
            </label>

            <label className="block text-sm font-semibold text-slate-200">
              Budget range
              <input
                className={inputClass}
                placeholder="15-20 Lacs"
                value={prefs.budget}
                onChange={(event) => setPrefs((prev) => ({ ...prev, budget: event.target.value }))}
              />
            </label>

            <label className="block text-sm font-semibold text-slate-200">
              Intake or study plan
              <input
                className={inputClass}
                placeholder="Fall 2026, Spring 2027"
                value={prefs.studyPlan}
                onChange={(event) => setPrefs((prev) => ({ ...prev, studyPlan: event.target.value }))}
              />
            </label>
          </div>

          {message && <p className="mt-4 text-sm text-cyan-100">{message}</p>}

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              onClick={save}
              disabled={saving}
              className="min-h-[48px] rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save Preferences"}
            </button>
            <Link
              to="/student/dashboard"
              className="min-h-[48px] rounded-xl border border-white/15 px-5 py-3 text-center text-sm font-semibold text-white transition hover:bg-white/[0.08]"
            >
              Back to dashboard
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}
